import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, Sparkles, Eye, DollarSign, BarChart3, Image as ImageIcon, MessageSquare, Flame } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { ShadowCard } from '../components/Feed/ShadowCard';
import { PostCard } from '../components/Feed/PostCard';

export const CreatorProgram = () => {
    const navigate = useNavigate();
    const currentUser = auth.currentUser;
    const [activeTab, setActiveTab] = useState('universal');
    
    const [shadows, setShadows] = useState([]);
    const [realityPosts, setRealityPosts] = useState([]);
    
    const [totalShadowViews, setTotalShadowViews] = useState(0);
    const [totalRealityViews, setTotalRealityViews] = useState(0);
    
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!currentUser) return;
            try {
                // Fetch User Profile for PostCard
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                if (userDoc.exists()) {
                    setUserProfile(userDoc.data());
                }

                // Fetch Shadows
                const qShadows = query(
                    collection(db, 'confessions'),
                    where('userId', '==', currentUser.uid),
                    orderBy('createdAt', 'desc')
                );
                const snapShadows = await getDocs(qShadows);
                let sViews = 0;
                const fetchedShadows = snapShadows.docs.map(doc => {
                    const data = doc.data();
                    sViews += (data.views || 0);
                    return { id: doc.id, ...data };
                });
                setShadows(fetchedShadows);
                setTotalShadowViews(sViews);

                // Fetch Reality Posts
                const qReality = query(
                    collection(db, 'sparkposts'),
                    where('uid', '==', currentUser.uid),
                    orderBy('createdAt', 'desc')
                );
                const snapReality = await getDocs(qReality);
                let rViews = 0;
                const fetchedReality = snapReality.docs.map(doc => {
                    const data = doc.data();
                    rViews += (data.views || 0);
                    return { id: doc.id, ...data };
                });
                setRealityPosts(fetchedReality);
                setTotalRealityViews(rViews);

            } catch (err) {
                console.error("Failed to fetch creator data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUser]);

    const shadowEarnings = (totalShadowViews / 1000) * 0.10;
    const realityEarnings = (totalRealityViews / 1000) * 0.10;
    
    const totalViews = totalShadowViews + totalRealityViews;
    const totalEarnings = shadowEarnings + realityEarnings;

    return (
        <main className="flex-1 w-full flex flex-col bg-transparent">
            {/* Header */}
            <div className="sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-md z-40 border-b border-[#1a1a1a] flex flex-col px-4 pt-2">
                <div className="flex items-center h-[50px]">
                    <button onClick={() => navigate('/settings')} className="sm:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2">
                        <ChevronLeft size={24} className="text-white" />
                    </button>
                    <h1 className="text-xl font-bold text-white flex items-center gap-2">
                        Creator Program <Sparkles size={20} className="text-[#0BD9E5]" />
                    </h1>
                </div>
                
                {/* Tabs */}
                <div className="flex w-full mt-2 border-b border-[#1a1a1a]">
                    <button 
                        onClick={() => setActiveTab('universal')}
                        className={`flex-1 py-4 text-[14px] sm:text-[15px] font-bold text-center relative transition-colors ${activeTab === 'universal' ? 'text-white' : 'text-gray-500 hover:bg-white/5'}`}
                    >
                        Universal
                        {activeTab === 'universal' && (
                            <div className="absolute bottom-0 left-0 w-full h-[4px] bg-gradient-to-r from-[#0BD9E5] to-[#BD00FF] rounded-t-full" />
                        )}
                    </button>
                    <button 
                        onClick={() => setActiveTab('shadows')}
                        className={`flex-1 py-4 text-[14px] sm:text-[15px] font-bold text-center relative transition-colors ${activeTab === 'shadows' ? 'text-white' : 'text-gray-500 hover:bg-white/5'}`}
                    >
                        Shadows
                        {activeTab === 'shadows' && (
                            <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#0BD9E5] rounded-t-full" />
                        )}
                    </button>
                    <button 
                        onClick={() => setActiveTab('reality')}
                        className={`flex-1 py-4 text-[14px] sm:text-[15px] font-bold text-center relative transition-colors ${activeTab === 'reality' ? 'text-white' : 'text-gray-500 hover:bg-white/5'}`}
                    >
                        Reality Mode
                        {activeTab === 'reality' && (
                            <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#BD00FF] rounded-t-full" />
                        )}
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                {activeTab === 'universal' && (
                    <div className="p-6">
                        {/* Universal Dashboard Stats */}
                        <div className="bg-gradient-to-br from-[#0BD9E5]/10 to-[#BD00FF]/10 border border-[#0BD9E5]/20 rounded-3xl p-6 flex flex-col items-center justify-center relative overflow-hidden mb-8">
                            <div className="absolute top-0 right-0 p-6 opacity-10">
                                <DollarSign size={100} />
                            </div>
                            <span className="text-white/80 text-sm font-bold uppercase tracking-wider mb-2">Total Combined Earnings</span>
                            <span className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#0BD9E5] to-[#BD00FF] mb-2">
                                ${totalEarnings.toFixed(2)}
                            </span>
                            <span className="text-gray-400 text-sm flex items-center gap-2">
                                <Eye size={14} /> {totalViews.toLocaleString()} Total Views
                            </span>
                        </div>

                        <h3 className="text-white font-bold text-lg mb-4">Earnings Breakdown</h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Shadows Card */}
                            <div className="bg-[#111] border border-[#222] rounded-2xl p-5 relative overflow-hidden flex flex-col">
                                <div className="absolute top-0 right-0 p-4 opacity-5">
                                    <MessageSquare size={60} />
                                </div>
                                <span className="text-gray-400 font-semibold mb-2">Shadows Mode</span>
                                <span className="text-2xl font-bold text-white mb-1">${shadowEarnings.toFixed(2)}</span>
                                <span className="text-[#0BD9E5] text-sm">{totalShadowViews.toLocaleString()} Views</span>
                            </div>

                            {/* Reality Card */}
                            <div className="bg-[#111] border border-[#222] rounded-2xl p-5 relative overflow-hidden flex flex-col">
                                <div className="absolute top-0 right-0 p-4 opacity-5">
                                    <ImageIcon size={60} />
                                </div>
                                <span className="text-gray-400 font-semibold mb-2">Reality Mode</span>
                                <span className="text-2xl font-bold text-white mb-1">${realityEarnings.toFixed(2)}</span>
                                <span className="text-[#BD00FF] text-sm">{totalRealityViews.toLocaleString()} Views</span>
                            </div>
                        </div>

                        {/* Info Banner */}
                        <div className="mt-8 bg-[#161616] rounded-xl p-4 flex items-start gap-3 border border-[#333]">
                            <div className="p-2 bg-[#222] rounded-lg mt-0.5">
                                <BarChart3 size={18} className="text-[#0BD9E5]" />
                            </div>
                            <div>
                                <h4 className="text-white font-semibold mb-1">Creator Program Rates</h4>
                                <p className="text-sm text-gray-400 leading-relaxed">
                                    You earn a flat rate of <strong>$0.10 for every 1,000 views</strong> on both Shadows and Reality Mode posts. Payouts are processed monthly when your balance exceeds $10.00.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'shadows' && (
                    <div className="px-4 py-6">
                        <div className="flex items-center justify-between mb-6 px-2">
                            <h3 className="text-white font-bold text-lg">Your Monetized Shadows</h3>
                            <span className="text-[#0BD9E5] font-bold text-sm bg-[#0BD9E5]/10 px-3 py-1 rounded-full">
                                {totalShadowViews.toLocaleString()} Views = ${shadowEarnings.toFixed(2)}
                            </span>
                        </div>
                        
                        {loading ? (
                            <div className="flex justify-center p-10">
                                <div className="w-8 h-8 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
                            </div>
                        ) : shadows.length === 0 ? (
                            <div className="text-center p-10 text-gray-500">
                                You haven't posted any shadows yet.
                            </div>
                        ) : (
                            shadows.map(shadow => (
                                <ShadowCard 
                                    key={shadow.id} 
                                    shadow={shadow} 
                                    isLiked={false} 
                                    onLikeUpdate={() => {}}
                                />
                            ))
                        )}
                    </div>
                )}

                {activeTab === 'reality' && (
                    <div className="px-4 py-6">
                        <div className="flex items-center justify-between mb-6 px-2">
                            <h3 className="text-white font-bold text-lg">Your Monetized Posts</h3>
                            <span className="text-[#BD00FF] font-bold text-sm bg-[#BD00FF]/10 px-3 py-1 rounded-full">
                                {totalRealityViews.toLocaleString()} Views = ${realityEarnings.toFixed(2)}
                            </span>
                        </div>
                        
                        {loading ? (
                            <div className="flex justify-center p-10">
                                <div className="w-8 h-8 rounded-full border-t-2 border-[#BD00FF] animate-spin" />
                            </div>
                        ) : realityPosts.length === 0 ? (
                            <div className="text-center p-10 text-gray-500">
                                You haven't posted any reality posts yet.
                            </div>
                        ) : (
                            realityPosts.map(post => (
                                <div 
                                    key={post.id} 
                                    onClickCapture={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        toast.info("Download the mobile app to view reality mode posts");
                                    }}
                                    className="cursor-pointer"
                                >
                                    <PostCard 
                                        post={post} 
                                        userProfile={userProfile}
                                        isLiked={false} 
                                        isSaved={false}
                                        onLikeUpdate={() => {}}
                                        onSaveUpdate={() => {}}
                                    />
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </main>
    );
};

export default CreatorProgram;
